/*
  <sj-nav>: desktop mega panels for sections/sj-header.liquid.
  - Hover (data-trigger="hover", fine pointers only) opens after a short intent delay and closes when the pointer
    leaves the item and its panel. The caret button always toggles, so keyboard and touch work too.
  - Escape closes and returns focus to the toggle; focus leaving the nav closes it.
  - Adds body.has-dropdown-menu while open so the theme's page overlay dims the page.
  - In the theme editor, selecting a Panel block opens its panel.
*/
class SjNav extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.sjn__item')].filter((item) => item.querySelector('.sjn-panel'));
    this.hover = this.dataset.trigger === 'hover' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.timer = null;

    this.items.forEach((item) => {
      const toggle = item.querySelector('.sjn__toggle');
      toggle.addEventListener('click', () => (item.classList.contains('is-open') ? this.close() : this.open(item)));
      if (this.hover) {
        item.addEventListener('pointerenter', () => this.schedule(() => this.open(item), this.current ? 0 : 120));
        item.addEventListener('pointerleave', () => this.schedule(() => this.close(), 220));
      }
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.current) return;
      const toggle = this.current.querySelector('.sjn__toggle');
      this.close();
      toggle.focus();
    });
    this.addEventListener('focusout', (event) => {
      if (this.current && !this.contains(event.relatedTarget)) this.close();
    });
    this.onDocClick = (event) => {
      if (this.current && !this.contains(event.target)) this.close();
    };
    document.addEventListener('click', this.onDocClick);

    if (window.Shopify && Shopify.designMode) {
      this.onBlockSelect = (event) => {
        const item = this.items.find((el) => el.contains(event.target) || el === event.target);
        if (item) this.open(item);
      };
      this.onBlockDeselect = () => this.close();
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    if (this.onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  schedule(fn, delay) {
    clearTimeout(this.timer);
    this.timer = setTimeout(fn, delay);
  }

  open(item) {
    clearTimeout(this.timer);
    if (this.current === item) return;
    if (this.current) this.setState(this.current, false);
    this.setState(item, true);
    this.current = item;
    document.body.classList.add('has-dropdown-menu');
  }

  close() {
    clearTimeout(this.timer);
    if (!this.current) return;
    this.setState(this.current, false);
    this.current = null;
    document.body.classList.remove('has-dropdown-menu');
  }

  setState(item, open) {
    const panel = item.querySelector('.sjn-panel');
    if (open && panel.classList.contains('sjn-panel--compact')) {
      const header = this.closest('header');
      const left = item.getBoundingClientRect().left - header.getBoundingClientRect().left - 16;
      panel.style.left = `${Math.max(0, Math.round(left))}px`;
    }
    item.classList.toggle('is-open', open);
    item.querySelector('.sjn__toggle').setAttribute('aria-expanded', String(open));
    panel.dataset.open = String(open);
    panel.inert = !open;
  }
}

if (!customElements.get('sj-nav')) customElements.define('sj-nav', SjNav);
